import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import PlanExplorerDialog from '@/components/dashboard/hub/PlanExplorerDialog'
import { logPaywallShown, usePaywallScope } from '@/lib/observability/paywall'
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowRight01Icon, Cancel01Icon, SparklesIcon } from "@hugeicons/core-free-icons"

interface PaywallModalProps {
  isOpen: boolean
  onClose: () => void
  propertyId: string
}

/**
 * Closeable paywall modal for demo site
 * Shows users they're viewing demo data and encourages them to start a trial
 */
export default function PaywallModal({ isOpen, onClose, propertyId }: PaywallModalProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const paywallScope = usePaywallScope()
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const [showPricingModal, setShowPricingModal] = useState(false)
  const prevOpen = useRef(false)

  useEffect(() => {
    if (isOpen && !prevOpen.current) {
      logPaywallShown({
        surface: 'demo_modal',
        trigger: 'demo',
        pathname: location.pathname,
        scope: { ...paywallScope, propertyId },
      })
    }
    prevOpen.current = isOpen
  }, [isOpen, location.pathname, paywallScope, propertyId])

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem(`demo-paywall-dismissed-${propertyId}`, 'true')
    }
    onClose()
  }

  const handleStartTrial = () => {
    handleClose()
    // Open the full pricing modal instead of navigating to a page
    setShowPricingModal(true)
  }

  const handleAddYourSite = () => {
    handleClose()
    navigate('/workspace/new')
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[500px] p-0 gap-0 overflow-hidden">
        {/* Header with gradient background */}
        <div className="p-6 pb-4 relative">
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 h-8 w-8"
            onClick={handleClose}
          >
            <HugeiconsIcon icon={Cancel01Icon} size={16} />
          </Button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <HugeiconsIcon icon={SparklesIcon} size={20} className="text-foreground/70" />
            </div>
            <DialogTitle className="text-xl font-semibold">
              You're Viewing Demo Data
            </DialogTitle>
          </div>
          <DialogDescription className="text-sm text-muted-foreground">
            This is sample data to help you explore Humblytics features.
          </DialogDescription>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="space-y-3">
            <p className="text-sm text-foreground">
              To unlock the full power of Humblytics with your own website data:
            </p>

            <div className="space-y-2">
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-semibold text-primary">1</span>
                </div>
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-medium text-foreground">
                    Add Your Website
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Connect your site and start tracking real visitor data
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-semibold text-primary">2</span>
                </div>
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-medium text-foreground">
                    Start Your Free Trial
                  </p>
                  <p className="text-xs text-muted-foreground">
                    14-day free trial with all Business plan features
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-2 pt-2">
            <Button
              className="w-full bg-primary hover:bg-primary/90 text-white"
              onClick={handleStartTrial}
            >
              Start free trial
              <HugeiconsIcon icon={ArrowRight01Icon} size={16} className="ml-2" />
            </Button>

            <Button
              variant="outline"
              className="w-full"
              onClick={handleAddYourSite}
            >
              Add Your Website
            </Button>
          </div>

          {/* Don't show again checkbox */}
          <div className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              id="dont-show-again"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-4 h-4 rounded border-border text-primary focus:ring-primary dark:bg-muted"
            />
            <label
              htmlFor="dont-show-again"
              className="text-xs text-muted-foreground cursor-pointer select-none"
            >
              Don't show this again for the demo site
            </label>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Full pricing modal */}
    <PlanExplorerDialog
      open={showPricingModal}
      onOpenChange={setShowPricingModal}
    />
  </>
  )
}

